/**
 * @file SectionLookTab.tsx
 * @description Вкладка «Оформление» секции (phase19.16 Р2; uiux-design-r2
 * §2): группы Фон · Ширина и высота · Колонки · Отступы · Рисунки ·
 * Телефон и видимость · Появление. Число колонок меняется только здесь.
 *
 * SB122 (uiux-settings-groups §1–§2, §5 шаг 4, §8): группы-карточки
 * `SettingsGroups panel="section-look"`. От 768 открыты все, кроме редких
 * «Рисунков» и «Появления»; сверху — оглавление-пилюли (7 групп); свёрнутые
 * помнятся на устройстве. До 768 открыта одна группа. Прежняя память
 * `commora.section-look.group` переносится один раз (запасной вариант для
 * первой открытой группы на телефоне). Переход к группе извне («изменить ›»
 * из «Содержимого», фото фона из заготовки) — `focusGroup` → общий
 * `OpenGroupRequest`. Ревью партии SB122: группа с замечанием — ✕ в
 * заголовке; неудачное «Сохранить блок» раскрывает первую такую.
 * SB120 этап 1 (uiux-sb120-stage1 §2, §6): группа с предупреждением
 * оформления (`sectionLookWarnings`) — «!» в заголовке, сводки — числами.
 */

import { DEFAULT_SITE_THEME, type SectionPayload } from "@chaiberi/shared";
import { useState } from "react";

import { useSectionForm } from "./section-form-context";

import { OpenGroupRequest, SettingsGroup, SettingsGroups, settingsGroupsKey } from "../../ui/settings-groups";
import type { ConfirmRequest } from "../../ui/use-confirm";
import { AppearanceForm, EFFECT_LABELS } from "../../site-pages/AppearanceForm";
import { DecorLayerList } from "./look/DecorLayerList";
import { LookBackgroundGroup } from "./look/LookBackgroundGroup";
import { LookColumnsGroup } from "./look/LookColumnsGroup";
import { LookPhoneGroup } from "./look/LookPhoneGroup";
import { LookSizeGroup } from "./look/LookSizeGroup";
import { LookSpaceGroup } from "./look/LookSpaceGroup";
import { RevealIssueGroup } from "./RevealIssueGroup";
import { useOutsideFrameState } from "../../../hooks/site-builder/useOutsideFrameState";
import { useSectionIssueGroups } from "../../../hooks/site-builder/useSectionIssueGroups";
import { groupsWithIssues, LOOK_ISSUE_RULES, type LookIssueGroup } from "../../../lib/site-builder/issue-groups";
import { DEFAULT_SPACE_THEME, type SpaceTheme } from "../../../lib/site-builder/look-space";
import { attentionWarnings, warningReason } from "../../../lib/site-builder/look-warnings";
import type { BlockAppearance } from "../../../lib/api-site-pages";
import { bgSummary, clipSummary, columnsSummary, decorSummary, migrateLookGroupMemory, phoneSummary, sizeSummary, spaceSummary } from "../../../lib/site-builder/look-summary";

/** Сводка до 40 знаков, полностью — в подсказке (uiux-sb120-stage1 §2). */
function Summary({ text }: { text: string }) {
  return <span title={text}>{clipSummary(text)}</span>;
}

/** Ключ панели групп: память `commora.settings-groups.section-look`. */
export const SECTION_LOOK_PANEL = "section-look";
/** Редкие группы — свёрнуты по умолчанию на ПК (uiux-settings-groups §1). */
const LOOK_COLLAPSED = ["decor", "appear"] as const;

interface Props {
  payload: SectionPayload;
  onChange: (payload: SectionPayload) => void;
  appearance: BlockAppearance;
  onAppearance: (appearance: BlockAppearance) => void;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  /** Сразу предложить фото фона (заготовка с фото-фоном). */
  askPhoto?: boolean;
  onAsked?: () => void;
  onPageBackground?: () => void;
  /** Раскрыть и показать группу (`"columns"` из «изменить ›»); после — `onFocused`. */
  focusGroup?: string | null;
  onFocused?: () => void;
}

export function SectionLookTab({ payload, onChange, appearance, onAppearance, confirm, askPhoto, onAsked, onPageBackground, focusGroup = null, onFocused }: Props) {
  // Прежняя память — до первого чтения памяти групп (корень читает её при создании).
  useState(() => migrateLookGroupMemory(settingsGroupsKey(SECTION_LOOK_PANEL)));
  const look = payload.look;
  const setLook = (next: SectionPayload["look"]) => onChange({ ...payload, look: next });
  // Фото фона из заготовки — сразу к «Фону».
  const target = askPhoto ? "bg" : focusGroup;
  const bad = useSectionIssueGroups(LOOK_ISSUE_RULES);
  const { lookWarnings, theme } = useSectionForm();
  const warned = groupsWithIssues(attentionWarnings(lookWarnings).map((w) => w.path), LOOK_ISSUE_RULES);
  // «!» у «Ширины и высоты» и при «Фон за рамку» + «Слить с блоком выше», когда верхний блок в рамке (SB120 этап 2, SB122).
  const outside = useOutsideFrameState(look, theme).state;
  const warnedGroup = (id: LookIssueGroup) => warned.has(id) || (id === "size" && outside.mergeWarning);
  const status = (id: LookIssueGroup) => (bad.has(id) ? ("error" as const) : warnedGroup(id) ? ("warning" as const) : undefined);
  const spaceTheme: SpaceTheme = theme ? { density: theme.spacing.density, phoneFactor: theme.spacing.phoneFactor } : DEFAULT_SPACE_THEME;

  return (
    <SettingsGroups panel={SECTION_LOOK_PANEL} defaultCollapsed={LOOK_COLLAPSED}>
      <OpenGroupRequest id={target} onDone={onFocused} />
      <RevealIssueGroup rules={LOOK_ISSUE_RULES} />
      <SettingsGroup id="bg" status={status("bg")} title="Фон" summary={<Summary text={[bgSummary(look.bg), warningReason(lookWarnings, "look.bg")].filter(Boolean).join(" · ")} />} hint="Прозрачный, цвет из оформления сайта или фото.">
        <LookBackgroundGroup look={look} onChange={setLook} askPhoto={askPhoto} onAsked={onAsked} onPageBackground={onPageBackground} />
      </SettingsGroup>
      <SettingsGroup id="size" status={status("size")} title="Ширина и высота" tocLabel="Размер" summary={<Summary text={sizeSummary(look, (theme ?? DEFAULT_SITE_THEME).frame)} />} hint="Ширина, высота и где стоит содержимое — числом.">
        <LookSizeGroup look={look} onChange={setLook} />
      </SettingsGroup>
      <SettingsGroup id="columns" status={status("columns")} title="Колонки" summary={<Summary text={columnsSummary(payload.columns, spaceTheme.density)} />} hint="Сколько колонок и в каких долях.">
        <LookColumnsGroup payload={payload} onChange={onChange} confirm={confirm} />
      </SettingsGroup>
      <SettingsGroup id="space" status={status("space")} title="Отступы" summary={<Summary text={spaceSummary(look.space)} />} hint="Числом в px; под каждым — сколько на телефоне. Наведите на поле — на сайте подсветится.">
        <LookSpaceGroup look={look} theme={spaceTheme} onChange={(space) => setLook({ ...look, space })} />
      </SettingsGroup>
      <SettingsGroup id="decor" status={status("decor")} title="Рисунки" summary={decorSummary(look.decor)} hint="Веточки, цветы и пятна поверх фона — до трёх.">
        <DecorLayerList decor={look.decor} columns={payload.columns.items.length} onChange={(decor) => setLook({ ...look, decor })} />
      </SettingsGroup>
      <SettingsGroup id="phone" status={status("phone")} title="Телефон и видимость" tocLabel="Видимость" summary={<Summary text={phoneSummary(look, payload.columns)} />} hint="Порядок колонок на телефоне и где показывать блок.">
        <LookPhoneGroup payload={payload} onChange={onChange} />
      </SettingsGroup>
      <SettingsGroup id="appear" title="Появление" summary={EFFECT_LABELS[appearance.effect ?? "none"].toLowerCase()} hint="Как блок появляется при прокрутке.">
        <AppearanceForm value={appearance} onChange={onAppearance} />
      </SettingsGroup>
    </SettingsGroups>
  );
}
